/**
 * ShopeeOrderBanner — Shopee actions card for the order detail sidebar.
 *
 * Shows Ship Order button when shopee_order_status === "READY_TO_SHIP",
 * reprint label when shipped, and tracking info with copy.
 *
 * Returns null for non-Shopee orders — zero impact on other order types.
 *
 * Design: matches OrderDetailSidebar cards — CARD token, p-5 sections,
 * FieldLabel typography, consistent with ContactCard/AddressesCard.
 */

import { useState } from "react";
import { Check, Copy, Package, Printer, Truck } from "lucide-react";
import { cn } from "@/lib/utils";
import { CARD } from "@/config/design-tokens";
import { toast } from "sonner";
import { ShopeeShippingSheet } from "@/features/orders/components/ShopeeShippingSheet";

interface ShopeeOrderBannerProps {
  formData: Record<string, any>;
  salesOrderName: string;
}

/** Statuses where Reprint Label works (Shopee still serves the label PDF) */
const REPRINT_STATUSES = ["SHIPPED", "PROCESSED"];
export function ShopeeOrderBanner({
  formData,
  salesOrderName,
}: ShopeeOrderBannerProps) {
  const [shippingSheetOpen, setShippingSheetOpen] = useState(false);
  const [reprintOpen, setReprintOpen] = useState(false);
  const [trackingCopied, setTrackingCopied] = useState(false);

  const isShopee = formData.source_channel === "Shopee";
  if (!isShopee) return null;

  const shopeeStatus = (formData.shopee_order_status as string) || "";
  const shopeeOrderSn = (formData.shopee_order_sn as string) || "";
  const trackingNo = (formData.shopee_tracking_no as string) || "";
  const carrier = (formData.shopee_shipping_carrier as string) || "";

  const isReadyToShip = shopeeStatus === "READY_TO_SHIP";
  const canReprint = REPRINT_STATUSES.includes(shopeeStatus);
  const isCancelled = shopeeStatus === "CANCELLED" || shopeeStatus === "IN_CANCEL";

  const hasTracking = !!(trackingNo || carrier);
  const hasActions = (isReadyToShip && !isCancelled) || canReprint;

  // Nothing useful to show
  if (!hasTracking && !hasActions) return null;

  const handleCopyTracking = () => {
    if (!trackingNo) return;
    navigator.clipboard.writeText(trackingNo).then(() => {
      setTrackingCopied(true);
      toast.success("Tracking number copied");
      setTimeout(() => setTrackingCopied(false), 2000);
    });
  };

  return (
    <>
      <div className={CARD}>
        <div className="p-5">
          <span className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
            Shopee Actions
          </span>

          {/* Tracking info */}
          {hasTracking && (
            <div className="mt-3 space-y-2">
              {carrier && (
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-0.5">Carrier</p>
                  <div className="flex items-center gap-1.5">
                    <Truck className="h-3.5 w-3.5 text-gray-400" />
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{carrier}</span>
                  </div>
                </div>
              )}
              {trackingNo && (
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-0.5">Tracking No.</p>
                  <button
                    onClick={handleCopyTracking}
                    className="flex items-center gap-1.5 group"
                  >
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{trackingNo}</span>
                    {trackingCopied ? (
                      <Check className="h-3 w-3 text-green-500" />
                    ) : (
                      <Copy className="h-3 w-3 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors" />
                    )}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Action buttons */}
          {hasActions && (
            <div className={cn("flex flex-col gap-2", hasTracking && "mt-4")}>
              {isReadyToShip && !isCancelled && (
                <div>
                  <button
                    onClick={() => setShippingSheetOpen(true)}
                    className="flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-medium transition-colors"
                  >
                    <Package className="h-4 w-4" />
                    Ship Order
                  </button>
                  <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-2 leading-relaxed">
                    เลือกวิธีจัดส่ง (นัดรับ/ไปส่งเอง) และรูปแบบใบปะหน้า จากนั้นยืนยันเพื่อแจ้ง Shopee จัดส่ง สถานะจะเปลี่ยนเป็น Shipped
                  </p>
                </div>
              )}
              {canReprint && (
                <div>
                  <button
                    onClick={() => setReprintOpen(true)}
                    className="flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-lg border border-border hover:bg-muted/50 text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors"
                  >
                    <Printer className="h-4 w-4" />
                    Reprint Label
                  </button>
                  <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-2 leading-relaxed">
                    ดาวน์โหลดใบปะหน้าอีกครั้ง ออเดอร์จัดส่งแล้ว — ไม่มีการเปลี่ยนสถานะ
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Shopee Shipping Sheet (Ship Order flow) */}
      <ShopeeShippingSheet
        open={shippingSheetOpen}
        onOpenChange={setShippingSheetOpen}
        orderName={salesOrderName}
        shopeeOrderSn={shopeeOrderSn}
      />

      {/* Reprint — skips pickup options, goes straight to label format + download */}
      {reprintOpen && (
        <ShopeeShippingSheet
          open={reprintOpen}
          onOpenChange={setReprintOpen}
          orderName={salesOrderName}
          shopeeOrderSn={shopeeOrderSn}
          mode="reprint"
        />
      )}
    </>
  );
}
